// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Button } from '@carbon/react';
import { useAlarmBrief } from '../../hooks/useAlarmBrief';
import { useShellStore } from '../../stores/shellStore';
import CitationChip from '../../components/chat/CitationChip';
import AskEnergyFluoIcon from '../../components/shell/AskEnergyFluoIcon';
import type { Alarm } from '../../types/alarm';
import './AlarmDetail.css';

const CONFIDENCE_LABEL = { high: 'High', medium: 'Medium', low: 'Low' } as const;

/** Mapped alarm fields default to '' / 'Unknown' when the source had nothing. */
const known = (s?: string) => (s && s !== 'Unknown' ? s : undefined);

const causesLabel = (n: number) => (n === 1 ? 'Possible cause' : 'Possible causes');

/** Splits a trailing "[SOP]" / "[Manual]" source tag off a possible cause. */
const splitSourceTag = (cause: string) => {
  const m = /^(.*?)\s*\[(SOP|Manual)\]\s*$/s.exec(cause);
  return m ? { text: m[1], tag: m[2] } : { text: cause, tag: undefined };
};

/**
 * EnergyFluo AI card shared by the alarm and anomaly detail pages: the brief's
 * fault box -> root-cause box, with the SOP-derived alarm fields as fallback.
 * The confidence badge only shows for a real AI root cause. With none, the box lists
 * the SOP/manual possible causes (marked not confirmed) when the brief has any.
 */
export default function AlarmBriefCard({
  alarm,
  eventType,
}: {
  alarm: Alarm;
  eventType: 'alarm' | 'anomaly';
}) {
  const { brief, loading } = useAlarmBrief(alarm, eventType);
  const openAskHelios = useShellStore(s => s.openAskHelios);
  const pageAlarmContext = useShellStore(s => s.pageAlarmContext);

  const faultText =
    brief?.fault ||
    (loading
      ? 'Analyzing…'
      : known(alarm.ruleTitle) ||
        known(alarm.dxTag) ||
        known(alarm.summary) ||
        known(alarm.issue) ||
        alarm.id);
  const aiRootCause = brief?.root_cause || undefined;
  const possibleCauses = aiRootCause ? [] : (brief?.possible_causes ?? []).filter(c => c.trim());
  const rootCauseText = aiRootCause
    ? aiRootCause
    : loading
      ? 'Analyzing…'
      : possibleCauses.length
        ? undefined
        : 'No root cause found yet.';
  const confLabel = aiRootCause && brief ? CONFIDENCE_LABEL[brief.confidence] : undefined;

  return (
    <section className="ald-card" data-testid="alarm-guidance">
      <div className="ald-ai-head">
        <div className="ald-ai-brand">
          <span className="ald-ai-icon">
            <AskEnergyFluoIcon />
          </span>
          <span className="ald-ai-name">EnergyFluo AI</span>
        </div>
        <Button
          className="ald-ai-ask"
          size="sm"
          kind="tertiary"
          onClick={() => openAskHelios(pageAlarmContext)}
        >
          Ask EnergyFluo
        </Button>
      </div>
      <div className="ald-brief-fault" data-testid="alarm-fault">
        <div className="ald-brief-label ald-brief-label--fault">Fault</div>
        <p className="ald-brief-text">{faultText}</p>
        {alarm.ruleId && <div className="ald-brief-rule">{alarm.ruleId}</div>}
      </div>
      <div className="ald-brief-connector" aria-hidden="true" />
      <div className="ald-brief-cause" data-testid="alarm-root-cause">
        <div className="ald-brief-cause-head">
          <span className="ald-brief-label ald-brief-label--cause">
            {possibleCauses.length ? causesLabel(possibleCauses.length) : 'Root cause'}
            {possibleCauses.length > 0 && (
              <span className="ald-brief-unconfirmed"> (not confirmed)</span>
            )}
          </span>
          {confLabel && (
            <span
              className={`ald-brief-conf ald-brief-conf--${confLabel.toLowerCase()}`}
              data-testid="alarm-confidence"
            >
              {confLabel} confidence
            </span>
          )}
        </div>
        {rootCauseText && <p className="ald-brief-text">{rootCauseText}</p>}
        {possibleCauses.length > 0 && (
          <ul className="ald-brief-list" data-testid="alarm-possible-causes">
            {possibleCauses.map((cause, i) => {
              const { text, tag } = splitSourceTag(cause);
              return (
                <li key={`${i}-${cause}`}>
                  {text}
                  {tag && (
                    <>
                      {' '}
                      <CitationChip label={tag} />
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="ald-ai-disclaimer">
        EnergyFluo AI can make mistakes. Review before acting.
      </div>
    </section>
  );
}
